import React, { useState } from 'react';
import {
  CalendarClock,
  Sparkles,
  IdCard,
  BookOpenCheck,
  CalendarDays,
  UserPlus,
  FileDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import AIChatDrawer from './AIChatDrawer';
import {
  TrialClassDialog,
  ExamRegisterDialog,
  PtinHelpDialog,
  MeetingDialog,
  ReferFriendDialog,
  BrochureDialog,
} from './ActionDialogs';

const LeftActionPanel = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState(null); // 'trial' | 'chat' | 'ptin' | 'exam' | 'meeting' | 'refer' | 'brochure'

  const closeAll = () => setOpen(null);

  const actions = [
    { key: 'trial', icon: CalendarClock, label: 'Book a Trial Class', sub: 'Free 45-min live session', desc: 'Sit in on a real EA class before you enroll. Meet the instructor, see the curriculum, and try the LMS.' },
    { key: 'chat', icon: Sparkles, label: 'Ask EA Assistant', sub: 'Instant answers · DeepSeek AI', desc: 'Chat with our AI trained on the SEE syllabus, IRS updates and Eduvantage FAQs. Free, 24x7.' },
    { key: 'ptin', icon: IdCard, label: 'Apply for PTIN', sub: 'IRS Preparer Tax ID · 15 min', desc: 'We help you file the IRS PTIN application end-to-end — documents, ITIN guidance, and the $19.75 fee.' },
    { key: 'exam', icon: BookOpenCheck, label: 'Register for SEE Exam', sub: 'Prometric scheduling help', desc: 'Book your SEE Part 1 / 2 / 3 slot at any Prometric center in India with pre-exam coaching included.' },
    { key: 'meeting', icon: CalendarDays, label: 'Book a Meeting', sub: 'Zoho meeting · pick a slot', desc: 'Talk 1-on-1 with a counselor. Discuss fees, EMIs, career switch and placement paths.' },
    { key: 'refer', icon: UserPlus, label: 'Refer a Friend', sub: 'Earn ₹5,000 per referral', desc: 'Share Eduvantage. Your friend gets ₹5,000 off, you get ₹5,000 cash on their enrollment.' },
    { key: 'brochure', icon: FileDown, label: 'Download Brochure', sub: 'Syllabus + fees + placements', desc: 'PDF with the full 3-part SEE syllabus, instructor bios, fees, EMIs and placement stats.' },
  ];

  return (
    <>
      <div
        className="fixed left-4 md:left-6 top-1/2 -translate-y-1/2 z-40 transition-all duration-500"
        data-testid="left-action-panel"
      >
        <div className="relative flex flex-col items-center gap-1.5 bg-[#f5f1e6] rounded-[28px] shadow-lg border border-[#143324]/12 py-3 px-2">
          <button
            onClick={() => setCollapsed((c) => !c)}
            aria-label="Toggle panel"
            data-testid="left-panel-toggle"
            className="w-10 h-10 rounded-full text-[#143324] hover:bg-[#143324]/8 flex items-center justify-center transition-colors"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>

          <div
            className={`flex flex-col items-center gap-1.5 transition-all duration-500 ${
              collapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'max-h-[720px] opacity-100 overflow-visible'
            }`}
          >
            {actions.map((a, i) => {
              const Icon = a.icon;
              const isPrimary = i === 0; // Book a Trial Class = highlighted primary

              return (
                <button
                  key={a.key}
                  onClick={() => setOpen(a.key)}
                  aria-label={a.label}
                  data-testid={`left-action-${a.key}`}
                  className="relative group"
                >
                  {/* Icon bubble */}
                  <div
                    className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 ease-out group-hover:scale-110 group-hover:-translate-y-0.5 group-hover:shadow-md ${
                      isPrimary
                        ? 'bg-[#143324] text-[#f5f1e6] group-hover:bg-[#1c4732]'
                        : 'bg-white border border-[#143324]/10 text-[#143324] group-hover:bg-[#143324] group-hover:text-[#f5f1e6] group-hover:border-[#143324]'
                    }`}
                  >
                    <Icon
                      size={16}
                      strokeWidth={1.7}
                      className="transition-transform duration-500 group-hover:rotate-[8deg]"
                    />
                    {isPrimary && (
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[#143324] opacity-40 pulse-dot" />
                    )}
                  </div>

                  {/* Description popup */}
                  <div
                    className="absolute left-full ml-4 top-1/2 -translate-y-1/2 w-[248px] bg-[#143324] text-[#f5f1e6] rounded-xl px-4 py-3 text-left opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 pointer-events-none transition-all duration-300 ease-out shadow-2xl"
                  >
                    <div className="font-serif-display text-[15px] leading-tight">{a.label}</div>
                    {a.sub && (
                      <div className="text-[10.5px] tracking-wider uppercase text-[#c9c2a8] mt-1 font-body">
                        {a.sub}
                      </div>
                    )}
                    {a.desc && (
                      <div className="text-[12px] leading-snug text-[#e8e2d1]/90 mt-2 font-body">
                        {a.desc}
                      </div>
                    )}
                    <div className="text-[11px] text-[#c9c2a8] mt-2.5 font-body inline-flex items-center gap-1">
                      Click to open
                      <ChevronRight size={11} className="animate-pulse" />
                    </div>
                    <span className="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-[#143324]" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Dialogs / Drawer */}
      <TrialClassDialog open={open === 'trial'} onClose={closeAll} />
      <AIChatDrawer open={open === 'chat'} onClose={closeAll} />
      <PtinHelpDialog open={open === 'ptin'} onClose={closeAll} />
      <ExamRegisterDialog open={open === 'exam'} onClose={closeAll} />
      <MeetingDialog open={open === 'meeting'} onClose={closeAll} />
      <ReferFriendDialog open={open === 'refer'} onClose={closeAll} />
      <BrochureDialog open={open === 'brochure'} onClose={closeAll} />
    </>
  );
};

export default LeftActionPanel;
